import type {SupabaseClient} from './supabaseTypes.js';

const PAGE_SIZE = 1000;

// The date filter every source read accepts. Both bounds are ISO date strings
// (`YYYY-MM-DD`) and both are optional; `null` means "unbounded on that side",
// which is why the defaults below are `null` rather than absent.
export type ProductionDateRange = {fromDate?: string | null; toDate?: string | null};

// `Array.isArray` narrows an `unknown` to `any[]`, so reading a jsonb column
// through it would leak `any` into every consumer of these results. Funnelling
// that one narrowing through a helper with a declared `unknown[]` return keeps
// the leak inside this line instead of spreading it across the module.
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

// `typeof x === 'object'` narrows `unknown` only to `object`, which has no
// readable `.message`. This predicate is that narrowing, written once.
function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function missingRelation(error: unknown): boolean {
  return /does not exist|schema cache|could not find/i.test(String((isObjectLike(error) && error.message) || ''));
}

// What fetchPaged needs of a query: a `.range()` that settles to PostgREST's
// {data, error} pair. Described STRUCTURALLY rather than by importing a
// PostgrestFilterBuilder type, because the client is created without generated
// database types (see supabaseTypes.ts) so the real builder's row type is `any`
// anyway -- and a structural shape keeps the caller free to hand over a fake.
type PagedResponse = {data: unknown[] | null; error: {message: string} | null};

// Production is now also a route-scoped Query resource. The same API still
// serves Home, so cancellation belongs in these shared wrappers rather than in
// one caller. The structural shape keeps the existing small test doubles valid:
// they need not implement abortSignal(), while a real Supabase builder does.
type AbortableRequest<Request> = {then: unknown; abortSignal?: (signal: AbortSignal) => Request};
type PagedQuery = {
  then: unknown;
  abortSignal?: (signal: AbortSignal) => PagedQuery;
  range(from: number, to: number): PromiseLike<PagedResponse>;
};

function withSignal<Request extends AbortableRequest<Request>>(request: Request, signal?: AbortSignal): Request {
  return signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Production source load aborted', 'AbortError');
}

async function fetchPaged(buildQuery: () => PagedQuery, signal?: AbortSignal): Promise<unknown[]> {
  let rows: unknown[] = [];
  let from = 0;
  while (true) {
    throwIfAborted(signal);
    const {data, error} = await withSignal(buildQuery(), signal).range(from, from + PAGE_SIZE - 1);
    throwIfAborted(signal);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows = rows.concat(data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return rows;
}

// One app_store row as `.select('key,data')` narrows it: `key` is the text
// primary key, `data` the jsonb payload — genuinely unknown until asArray()
// below checks it.
type AppStoreRow = {key: string; data: unknown};

async function fetchAppStoreProductionRows(
  sb: SupabaseClient,
  signal?: AbortSignal,
): Promise<{batches: unknown[]; feederGroups: unknown[]}> {
  const {data, error} = await withSignal(
    sb.from('app_store').select('key,data').in('key', ['ppp-v4', 'ppp-feeders-v1']),
    signal,
  );
  if (error) throw error;
  const store: Record<string, unknown> = Object.fromEntries(
    (data || []).map((row: AppStoreRow) => [row.key, row.data]),
  );
  return {
    batches: asArray(store['ppp-v4']),
    feederGroups: asArray(store['ppp-feeders-v1']),
  };
}

async function fetchLegacyEvents(
  sb: SupabaseClient,
  {fromDate = null, toDate = null}: ProductionDateRange = {},
  signal?: AbortSignal,
): Promise<unknown[]> {
  const {data, error} = await withSignal(
    sb.rpc('list_production_legacy_events', {
      p_from_date: fromDate,
      p_to_date: toDate,
    }),
    signal,
  );
  if (error) {
    if (missingRelation(error)) return [];
    throw error;
  }
  return data || [];
}

// These are intentionally two literal readers instead of one parameterized
// helper. The physical-call inventory cannot attribute a runtime table argument
// to either declared table, so keeping the reads separate makes the ownership
// proof precise without changing their query shape or fallback.
async function fetchCattleProcessingBatches(
  sb: SupabaseClient,
  {fromDate = null, toDate = null}: ProductionDateRange = {},
  signal?: AbortSignal,
): Promise<unknown[]> {
  return fetchPaged(() => {
    let query = sb
      .from('cattle_processing_batches')
      .select('*')
      .not('actual_process_date', 'is', null)
      .order('actual_process_date', {ascending: false});
    if (fromDate) query = query.gte('actual_process_date', fromDate);
    if (toDate) query = query.lte('actual_process_date', toDate);
    return query;
  }, signal).catch((error) => {
    if (missingRelation(error)) return [];
    throw error;
  });
}

async function fetchSheepProcessingBatches(
  sb: SupabaseClient,
  {fromDate = null, toDate = null}: ProductionDateRange = {},
  signal?: AbortSignal,
): Promise<unknown[]> {
  return fetchPaged(() => {
    let query = sb
      .from('sheep_processing_batches')
      .select('*')
      .not('actual_process_date', 'is', null)
      .order('actual_process_date', {ascending: false});
    if (fromDate) query = query.gte('actual_process_date', fromDate);
    if (toDate) query = query.lte('actual_process_date', toDate);
    return query;
  }, signal).catch((error) => {
    if (missingRelation(error)) return [];
    throw error;
  });
}

async function fetchEggDailys(
  sb: SupabaseClient,
  {fromDate = null, toDate = null}: ProductionDateRange = {},
  signal?: AbortSignal,
): Promise<unknown[]> {
  return fetchPaged(() => {
    let query = sb
      .from('egg_dailys')
      .select(
        'id,date,group1_name,group1_count,group2_name,group2_count,group3_name,group3_count,group4_name,group4_count',
      )
      .is('deleted_at', null)
      .order('date', {ascending: false});
    if (fromDate) query = query.gte('date', fromDate);
    if (toDate) query = query.lte('date', toDate);
    return query;
  }, signal).catch((error) => {
    if (missingRelation(error)) return [];
    throw error;
  });
}

// Every row payload stays `unknown[]`: these five reads are `select('*')`, a
// jsonb blob, or an RPC result, and the client has no generated database types
// to check a shape against. `sourceErrors` holds whatever each source rejected
// with — a PostgrestError for a live failure, anything for a thrown value.
export type ProductionSources = {
  batches: unknown[];
  feederGroups: unknown[];
  legacyEvents: unknown[];
  cattleProcessingBatches: unknown[];
  sheepProcessingBatches: unknown[];
  eggDailys: unknown[];
  sourceErrors: Record<string, unknown>;
};

// One failed source must never blank every program's totals. Each of the five
// independent reads settles on its own (Promise.allSettled, not Promise.all):
// a rejected source falls back to its empty shape and is recorded by key in
// `sourceErrors` so callers (Home, /production) can tell a genuine permission/
// network failure apart from a confirmed-empty result and render each program
// accordingly instead of collapsing the whole card to one blanket failure.
export async function loadProductionSources(
  sb: SupabaseClient,
  {fromDate = null, toDate = null}: ProductionDateRange = {},
  signal?: AbortSignal,
): Promise<ProductionSources> {
  throwIfAborted(signal);
  const [appStoreR, legacyR, cattleR, sheepR, eggR] = await Promise.allSettled([
    fetchAppStoreProductionRows(sb, signal),
    fetchLegacyEvents(sb, {fromDate, toDate}, signal),
    fetchCattleProcessingBatches(sb, {fromDate, toDate}, signal),
    fetchSheepProcessingBatches(sb, {fromDate, toDate}, signal),
    fetchEggDailys(sb, {fromDate, toDate}, signal),
  ]);
  // Promise.allSettled is deliberately the source-failure contract. An abort
  // is different: it belongs to a superseded scoped request and must reject so
  // Query does not cache an artificial all-sources-failed result.
  throwIfAborted(signal);

  const sourceErrors: Record<string, unknown> = {};
  function settle<T>(key: string, result: PromiseSettledResult<T>, empty: T): T {
    if (result.status === 'fulfilled') return result.value;
    sourceErrors[key] = result.reason;
    return empty;
  }

  const appStore = settle('appStore', appStoreR, {batches: [], feederGroups: []});
  const legacyEvents = settle('legacyEvents', legacyR, []);
  const cattleProcessingBatches = settle('cattle', cattleR, []);
  const sheepProcessingBatches = settle('sheep', sheepR, []);
  const eggDailys = settle('egg', eggR, []);

  return {
    ...appStore,
    legacyEvents,
    cattleProcessingBatches,
    sheepProcessingBatches,
    eggDailys,
    sourceErrors,
  };
}
